import React, { useState, useEffect } from 'react';
import {
  Routes,
  Route,
  useParams,
  Link,
  Navigate,
  useLocation,
} from "react-router-dom";import AlertPage from './components/AlertPage';
import AdminDashboard from './components/AdminDashboard';
import RegistrationForm from './components/RegistrationForm';
import ScanVehiclePage from './components/ScanVehiclePage';
import LoginPage from './components/LoginPage';
import { db } from './firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { AppBar, Toolbar, Typography, Button, Container, Box, IconButton, Menu, MenuItem, CircularProgress } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import PreGeneratedQRCodes from './components/PreGeneratedQRCodes';
import { resolveQrRoute } from './utils/qrFlow';

function AlertPageWithParams() {
  const { vehicleId } = useParams();
  return <AlertPage vehicleId={vehicleId} />;
}

function AlertPageWithQrParam() {
  const { qrId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [vehicleId, setVehicleId] = useState(null);
  const [qrLinked, setQrLinked] = useState(undefined); // undefined=checking, true/false after check

  useEffect(() => {
    let mounted = true;

    const fetch = async () => {
      if (!qrId) {
        if (mounted) {
          setVehicleId(null);
          setQrLinked(false);
        }
        return;
      }

      try {
        const q = query(collection(db, 'registrations'), where('qrCodeId', '==', qrId));
        const snap = await getDocs(q);
        if (!mounted) return;

        if (!snap.empty) {
          setVehicleId(snap.docs[0].data().vehicleNumber);
          setQrLinked(true);
        } else {
          setVehicleId(null);
          setQrLinked(false);
        }
      } catch (e) {
        if (mounted) {
          setVehicleId(null);
          setQrLinked(false);
        }
      }
    };
    fetch();

    return () => {
      mounted = false;
    };
  }, [qrId]);

  // While checking QR or auth state, show a loader inside AlertPage's layout
  if (qrLinked === undefined || authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // If QR is not linked, direct to registration flow.
  // If user is not logged in, the registration page will handle the login redirect.
  if (qrLinked === false) {
    const registerPath = `/register?qr=${encodeURIComponent(qrId)}`;
    return <Navigate to={registerPath} replace />;
  }

  // QR linked -> show alert page without requiring login
  return <AlertPage vehicleId={vehicleId} />;
}

function Home() {
  const { user, isAdmin } = useAuth();

  return (
    <Container maxWidth="md" sx={{ mt: 8 }}>
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography variant="h2" gutterBottom>GadiMitra</Typography>
        <Typography variant="h5" color="text.secondary" gutterBottom>
          Instantly alert vehicle owners in Bihar! Scan the QR code on any vehicle to notify the owner about wrong parking, emergencies, or to contact them directly.
        </Typography>
        <Box sx={{ mt: 4 }}>
          {/* <Button component={Link} to="/alert/DEMO123" variant="contained" size="large" sx={{ mr: 2 }}>Alert Vehicle</Button> */}
          <Button component={Link} to="/admin" variant="outlined" size="large">Admin Dashboard</Button>
        </Box>
        <Box sx={{ mt: 2 }}>
  {isAdmin && (
    <Button component={Link} to="/register" variant="text">
      Register Vehicle
    </Button>
  )}

  {isAdmin && (
    <Button component={Link} to="/login" variant="text">
      Admin Login
    </Button>
  )}
</Box>
      </Box>
    </Container>
  );
}

function Register() {
  const { isAdmin, user, loading } = useAuth();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const qr = params.get('qr') || params.get('qrId');
  const [qrRecord, setQrRecord] = useState(null);
  const [checkingQr, setCheckingQr] = useState(Boolean(qr));

  useEffect(() => {
    let isMounted = true;

    const checkQr = async () => {
      if (!qr) {
        if (isMounted) {
          setQrRecord(null);
          setCheckingQr(false);
        }
        return;
      }

      try {
        const registrationQuery = query(collection(db, 'registrations'), where('qrCodeId', '==', qr));
        const registrationSnap = await getDocs(registrationQuery);
        if (!isMounted) return;

        if (!registrationSnap.empty) {
          const registrationDoc = registrationSnap.docs[0];
          setQrRecord({ id: registrationDoc.id, ...registrationDoc.data(), linked: true });
          return;
        }

        setQrRecord(null);
      } catch (e) {
        if (isMounted) setQrRecord(null);
      } finally {
        if (isMounted) setCheckingQr(false);
      }
    };

    checkQr();

    return () => {
      isMounted = false;
    };
  }, [qr]);

  const routeDecision = resolveQrRoute({ qrId: qr, qrRecord, user, isAdmin });

  if (loading || checkingQr) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (routeDecision.type === 'alert') {
    return <Navigate to={routeDecision.to} replace />;
  }

  if (routeDecision.type === 'login') {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <RegistrationForm />
    </Container>
  );
}

function NavBar() {
  const { user, logout, isAdmin } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleMenu = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleLogout = async () => {
    await logout();
  };

  return (
    <AppBar position="static">
      <Container maxWidth="lg">
        <Toolbar>
          <Box component={Link} to="/" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}>
            <img
  src={`${process.env.PUBLIC_URL}/assets/logo.jpeg`}
  alt="GadiMitra Logo"style={{ height: '60px', width: 'auto', marginRight: '10px' }} />
            <Typography variant="h6" sx={{ color: 'inherit' }}>
              GadiMitra
            </Typography>
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
            <Button color="inherit" component={Link} to="/">Home</Button>
            {/* <Button color="inherit" component={Link} to="/alert/DEMO123">Alert Vehicle</Button> */}
            <Button color="inherit" component={Link} to="/admin">Admin Dashboard</Button>
            {isAdmin && <Button color="inherit" component={Link} to="/register">Register Vehicle</Button>}
            {user && <Button color="inherit" component={Link} to="/admin/qrcodes">Admin QR</Button>}
            {user ? (
              <>
                <Button color="inherit" onClick={handleLogout}>Logout</Button>
                <Typography sx={{ px: 1, display: 'flex', alignItems: 'center' }}>
                  {user.email}
                </Typography>
              </>
            ) : (
              <Button color="inherit" component={Link} to="/login">Login</Button>
            )}
          </Box>
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <IconButton color="inherit" onClick={handleMenu}><MenuIcon /></IconButton>
            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
              <MenuItem component={Link} to="/" onClick={handleClose}>Home</MenuItem>
              {/* <MenuItem component={Link} to="/alert/DEMO123" onClick={handleClose}>Alert Vehicle</MenuItem> */}
              <MenuItem component={Link} to="/admin" onClick={handleClose}>Admin Dashboard</MenuItem>
              {isAdmin && (
                <MenuItem component={Link} to="/register" onClick={handleClose}>Register Vehicle</MenuItem>
              )}
              {user && (
                <MenuItem component={Link} to="/admin/qrcodes" onClick={handleClose}>Admin QR</MenuItem>
              )}
              {user ? (
                <MenuItem onClick={() => { handleLogout(); handleClose(); }}>
                  Logout
                </MenuItem>
              ) : (
                <MenuItem component={Link} to="/login" onClick={handleClose}>Login</MenuItem>
              )}
            </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

function RequireAdmin({ children }) {
  const { user, loading, isAdmin } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (!isAdmin) return <Navigate to="/alert/DEMO123" replace />;
  return children;
}
function DefaultRoute() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '70vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return user
    ? <Navigate to="/admin" replace />
    : <Navigate to="/login" replace />;
}
function AppContent() {
  const location = useLocation();

  return (
    <>
      {location.pathname !== "/login" && <NavBar />}

      <Routes>
        <Route
          path="/"
          element={<DefaultRoute />}
        />

        <Route
          path="/login"
          element={<LoginPage />}
        />

        <Route
          path="/register"
          element={<Register />}
        />

        <Route
          path="/alert/:vehicleId"
          element={<AlertPageWithParams />}
        />

        <Route
          path="/alert/qr/:qrId"
          element={<AlertPageWithQrParam />}
        />

        <Route
          path="/scan/:registrationId"
          element={<ScanVehiclePage />}
        />

        <Route
          path="/admin"
          element={
            <RequireAuth>
              <AdminDashboard />
            </RequireAuth>
          }
        />

        <Route
          path="/admin/qrcodes"
          element={
            <RequireAuth>
              <PreGeneratedQRCodes />
            </RequireAuth>
          }
        />
      </Routes>
    </>
  );
}
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;



